import React, { MutableRefObject } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../root/RootReducer";
import { createStyles, makeStyles, Slider } from "@material-ui/core";
import { stringifyVideoTimeWithColons } from "../../../common/video/util/videoTimeStrings";
import styles from "./TimelinePrograssBar.module.css";
import { UpdateTimelinePlayInfo, UpdateTimelineSourceIndex } from "../../store/actions/TimelineActions";
import { GetClipCurrentTime, GetCurrentClipIndexFromTime } from "../../../utils/TimelineUtils";
import { isNull } from "../../../utils/ObjectUtils";

/**
|--------------------------------------------------
| タイムライン進捗バー
|--------------------------------------------------
*/

const useStyles = makeStyles(() =>
    createStyles({
        slider: {
            "&": {
                opacity: "0.7"
            },
            "& .MuiSlider-track": {
                opacity: "1",
                background: "red"
            },
            "& .MuiSlider-thumb[data-index=\"1\"]": {
                display: "none"
            },
            "& .MuiSlider-valueLabel span > span": {
                color: "#212126",
                fontSize: "16px"
            },
            "& .PrivateValueLabel-circle-15": {
                transform: "none",
                borderRadius: "5px",
                padding: "5px",
                minWidth: "32px",
                width: "auto",
                height: "auto"
            },
            "& .PrivateValueLabel-label-16": {
                transform: "none"
            }
        },
        isNone: {
            "& .MuiSlider-track": {
                left: "0%!important",
                width: "100%!important"
            },
            "& .MuiSlider-thumb": {
                left: "0%!important"
            }
        }
    })
);

interface PrograssBarProps {
    videoRefs: MutableRefObject<HTMLVideoElement[]>;
    handleReset: () => void;
}

export default function TimelinePrograssBar({
    videoRefs,
    handleReset
}: PrograssBarProps) {
    const dispatch = useDispatch();
    const classes = useStyles();
    const {
        clipList,
        sourceIndex,
        clipTimes,
        currentTime,
        totalTime,
        sourceLength,
        isPlay,
    } = useSelector((state: AppState) => ({
        clipList: state.timeline.timeline?.clipList,
        sourceIndex: state.timeline.timelinePlayInfo.sourceIndex,
        clipTimes: state.timeline.timelinePlayInfo.clipTimes,
        currentTime: state.timeline.timelinePlayInfo.currentTime,
        totalTime: state.timeline.timelinePlayInfo.totalTime,
        sourceLength: state.timeline.timelinePlayInfo.sourceLength,
        isPlay: state.timeline.timelinePlayInfo.isPlay,
    }));

    // ビデオ再生進捗バーの移動が停止した場合
    const handleChangeCommitted = (event: React.ChangeEvent<{}>, value: number | number[]) => {
        if (
            isNull(value) ||
            !clipList
        ) return;
        
        // プレイを一時停止し、タイムラインの再生時間を更新
        const draggedTime = Math.round((value as number) / 1000) * 1000;

        // 移動先のクリップIndexをsourceIndexにし、クリップ再生時間を設定
        if (clipTimes && videoRefs) {
            // クリップのstartをリセットする
            handleReset();

            const currentClipIndex = GetCurrentClipIndexFromTime(draggedTime, clipTimes, sourceLength);
            const clipCurrentTime = GetClipCurrentTime(draggedTime, clipTimes);
            const video = videoRefs.current[currentClipIndex];
            const videoStartTime = clipList[currentClipIndex].start;
            if (video) {
                video.currentTime = Math.round((videoStartTime + clipCurrentTime) / 1000);
                if (isPlay) video.play();
            }

            if (sourceIndex !== currentClipIndex)
                dispatch(UpdateTimelineSourceIndex(currentClipIndex));
        }
    };

    // ビデオ再生進捗バーを移動した場合
    const handleProgressBarChange = (event: React.ChangeEvent<{}>, value: number | number[]) => {
        if (
            isNull(value) ||
            !clipList ||
            Math.round((value as number) / 1000) * 1000 === currentTime
        ) return;
        
        // プレイを一時停止し、タイムラインの再生時間を更新
        const draggedTime = Math.round((value as number) / 1000) * 1000;
        const isFinishedFlag = draggedTime === totalTime ? true: false;

        dispatch(UpdateTimelinePlayInfo(
            draggedTime,
            null,
            null,
            null,
            isFinishedFlag
        ));

        // すべてのクリップを一時停止
        if (clipTimes && videoRefs) {
            for ( let i = 0; i < videoRefs.current.length; i++) {
                videoRefs.current[i].pause();
            }
        }
    };

    return (
        <div className={styles.progressSlider}>
            <div className={styles.durationMillis}>
                <div>{stringifyVideoTimeWithColons(currentTime)}</div>
                <div>{stringifyVideoTimeWithColons(totalTime)}</div>
            </div>

            <Slider
                min={0}
                max={totalTime}
                value={currentTime}
                onChange={handleProgressBarChange}
                onChangeCommitted={handleChangeCommitted}
                style={{
                    width: "100%",
                    color: "white",
                    fontWeight: "bold"
                }}
                className={clipList && clipList.length > 0 ? classes.slider : classes.isNone }
            />
        </div>
    );
}
